import React from "react";
import { Brain, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DeltaChip } from "../common/DeltaChip.jsx";
import { splitRankingHalves } from "../common/rankingUtils.js";
import {
  getTrainabilityDisplayColor,
  getTrainabilityDisplayLabel,
  getTrainabilityScore10,
  getTrainabilityTextStyle,
  hasTrainability,
  resolveTrainabilityLevel,
} from "../common/TrainabilityUtils";
import { PLAYER_AT_RISK_TRAINABILITY_THRESHOLD } from "../common/playerRiskUtils";

const DEFAULT_VISIBLE_PLAYERS = 4;
const TRAINABILITY_LEVEL_KEYS = [
  "underload",
  "suboptimal",
  "optimal",
  "supraoptimal",
  "overload",
];
const TRAINABILITY_SCORE_SLOT_REM = 5.5;
const TRAINABILITY_SCORE_GAP_REM = 0.5;
const TRAINABILITY_SLIDE_REM = TRAINABILITY_SCORE_SLOT_REM + TRAINABILITY_SCORE_GAP_REM;
const TRAINABILITY_VALUE_ROW_H = "h-12";
const TRAINABILITY_LABEL_MEASURE_CLASS =
  "whitespace-nowrap text-base md:text-lg font-bold leading-none";

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const getInitials = (player) =>
  `${player?.first_name?.[0] ?? ""}${player?.last_name?.[0] ?? ""}`;

const getTrainabilityDelta10 = (player) => {
  if (!hasTrainability(player?.trainability_index) || !hasTrainability(player?.trainability_index_prev)) {
    return null;
  }
  const current = getTrainabilityScore10(player.trainability_index);
  const previous = getTrainabilityScore10(player.trainability_index_prev);
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  return current - previous;
};

const toGaugeWidthPct = (score10) => {
  if (!Number.isFinite(score10)) return 0;
  return clamp((score10 / 10) * 100, 6, 100);
};

const getBarColor = (player, score10, isDarkMode) => {
  const status = resolveTrainabilityLevel(player?.trainability_level, {
    group: "global",
  });
  return (
    getTrainabilityDisplayColor({ score: score10, status }) ||
    (isDarkMode ? "#94a3b8" : "#64748b")
  );
};

export default function TrainabilityRankingSection({
  players,
  isDarkMode,
  onPlayerClick,
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);

  const entries = React.useMemo(
    () =>
      (players || [])
        .map((player) => {
          const score10 = getTrainabilityScore10(player?.trainability_index);
          if (!Number.isFinite(score10)) return null;
          return {
            player,
            score10,
            delta10: getTrainabilityDelta10(player),
          };
        })
        .filter(Boolean),
    [players]
  );

  const labelMeasureRef = React.useRef(null);
  const [maxLabelWidthPx, setMaxLabelWidthPx] = React.useState(null);

  const labelsToMeasure = React.useMemo(() => {
    const labels = new Set(
      TRAINABILITY_LEVEL_KEYS.map((key) => t(`trainability_levels.${key}`))
    );
    for (const entry of entries) {
      const { label } = getTrainabilityDisplayLabel({
        trainabilityLevelObj: entry.player?.trainability_level,
        score: entry.score10,
        t,
      });
      if (label) labels.add(label);
    }
    return [...labels];
  }, [entries, t]);

  React.useLayoutEffect(() => {
    const measureRoot = labelMeasureRef.current;
    if (!measureRoot) return;

    const measure = () => {
      let max = 0;
      measureRoot.querySelectorAll("[data-trainability-label]").forEach((node) => {
        max = Math.max(max, node.getBoundingClientRect().width);
      });
      setMaxLabelWidthPx(Math.ceil(max) + 4);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [labelsToMeasure]);

  const trainabilityValueWidth =
    maxLabelWidthPx != null
      ? `calc(${maxLabelWidthPx}px + ${TRAINABILITY_SLIDE_REM}rem)`
      : `calc(7.5rem + ${TRAINABILITY_SLIDE_REM}rem)`;

  if (!entries.length) return null;

  const {
    topVisible,
    lowestVisible,
    hasHiddenEntries,
  } = splitRankingHalves(entries, {
    getValue: (entry) => entry.score10,
    expanded,
    collapsedLimit: DEFAULT_VISIBLE_PLAYERS,
  });

  const avgScore10 =
    entries.reduce((sum, entry) => sum + entry.score10, 0) / entries.length;
  const atRiskCount = entries.filter(
    (entry) => entry.score10 < PLAYER_AT_RISK_TRAINABILITY_THRESHOLD
  ).length;

  const textureStyle = {
    backgroundImage: isDarkMode
      ? "radial-gradient(circle at 12% 10%, rgba(255,255,255,0.12), transparent 44%), radial-gradient(circle at 88% 84%, rgba(148,163,184,0.16), transparent 42%), repeating-linear-gradient(125deg, rgba(148,163,184,0.09) 0px, rgba(148,163,184,0.09) 1px, transparent 1px, transparent 8px)"
      : "radial-gradient(circle at 12% 10%, rgba(255,255,255,0.95), transparent 50%), radial-gradient(circle at 88% 84%, rgba(148,163,184,0.2), transparent 44%), repeating-linear-gradient(125deg, rgba(148,163,184,0.11) 0px, rgba(148,163,184,0.11) 1px, transparent 1px, transparent 7px)",
  };

  const renderRow = (entry, kind, index) => {
    const { player, score10, delta10 } = entry;
    const clickable = typeof onPlayerClick === "function";
    const displayName = `${player.first_name} ${player.last_name}`;
    const positionCode = player?.position
      ? String(player.position).slice(0, 3).toUpperCase()
      : null;
    const { level, label } = getTrainabilityDisplayLabel({
      trainabilityLevelObj: player?.trainability_level,
      score: score10,
      t,
    });
    const labelStyle = getTrainabilityTextStyle({
      level,
      score: score10,
      isDarkMode,
    });

    return (
      <div
        key={`${kind}-${player.id ?? `${displayName}-${index}`}`}
        onClick={() => clickable && onPlayerClick(player)}
        className={`group rounded-xl px-2 py-1.5 transition-colors ${
          clickable
            ? isDarkMode
              ? "cursor-pointer hover:bg-white/10"
              : "cursor-pointer hover:bg-white/70"
            : ""
        }`}
      >
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 shrink-0 rounded-full overflow-hidden bg-gradient-to-br from-indigo-500 to-sky-500 flex items-center justify-center text-xs font-semibold text-white">
            {player.profile_picture ? (
              <img
                src={`data:image/jpeg;base64,${player.profile_picture}`}
                alt={displayName}
                className="h-full w-full object-cover"
              />
            ) : (
              getInitials(player)
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className={`flex ${TRAINABILITY_VALUE_ROW_H} items-center justify-between gap-2`}>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{displayName}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[11px] opacity-75">
                  {positionCode && (
                    <span
                      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                        isDarkMode
                          ? "bg-white/10 text-slate-100"
                          : "bg-white/80 text-slate-700"
                      }`}
                    >
                      {positionCode}
                    </span>
                  )}
                  <span>
                    {player.timeinfo_date
                      ? `${t("metrics.last_sim")}: ${player.timeinfo_date}`
                      : `${t("metrics.last_sim")}: ${t("common.na")}`}
                  </span>
                </div>
              </div>

              <div
                className={`relative flex ${TRAINABILITY_VALUE_ROW_H} shrink-0 items-center justify-end`}
                style={{ width: trainabilityValueWidth }}
              >
                <span
                  className={`flex ${TRAINABILITY_VALUE_ROW_H} shrink-0 items-center justify-end whitespace-nowrap ${TRAINABILITY_LABEL_MEASURE_CLASS} transition-transform duration-300 ease-in-out group-hover:-translate-x-[6rem] ${
                    labelStyle.className ?? ""
                  }`}
                  style={{
                    width: maxLabelWidthPx ?? undefined,
                    minWidth: maxLabelWidthPx == null ? "5.5rem" : undefined,
                    ...(labelStyle.color ? { color: labelStyle.color } : {}),
                  }}
                >
                  {label ?? t("common.na")}
                </span>

                <div
                  className={`pointer-events-none absolute right-0 top-0 flex ${TRAINABILITY_VALUE_ROW_H} flex-col items-end justify-center gap-0.5 opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100`}
                  style={{ width: `${TRAINABILITY_SCORE_SLOT_REM}rem` }}
                >
                  <span className="whitespace-nowrap text-base font-semibold tabular-nums leading-none">
                    {score10.toFixed(1)}/10
                  </span>
                  {Number.isFinite(delta10) ? (
                    <DeltaChip
                      delta={delta10}
                      unit=" /10"
                      decimals={1}
                      label={t("metrics.last_session")}
                    />
                  ) : (
                    <span className="invisible inline-flex h-[22px]" aria-hidden />
                  )}
                </div>
              </div>
            </div>

            <div
              className={`mt-2 h-3.5 w-full rounded-full overflow-hidden relative ${
                isDarkMode ? "bg-slate-700/80" : "bg-white/90"
              }`}
            >
              <div
                className="h-full rounded-full shadow-[inset_0_1px_1px_rgba(255,255,255,0.35)]"
                style={{
                  width: `${toGaugeWidthPct(score10)}%`,
                  backgroundColor: getBarColor(player, score10, isDarkMode),
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section
      className={`relative overflow-hidden -mx-1 px-5 py-6 md:px-6 md:py-7 ${
        isDarkMode ? "bg-slate-900/55 text-white" : "bg-slate-200/65 text-slate-900"
      }`}
    >
      <div className="pointer-events-none absolute inset-0" style={textureStyle} />
      <div
        className={`pointer-events-none absolute inset-x-0 top-0 h-px ${
          isDarkMode
            ? "bg-gradient-to-r from-transparent via-white/35 to-transparent"
            : "bg-gradient-to-r from-transparent via-slate-500/45 to-transparent"
        }`}
      />

      <div className="relative">
        <div
          ref={labelMeasureRef}
          className="pointer-events-none fixed -left-[9999px] top-0 opacity-0"
          aria-hidden
        >
          {labelsToMeasure.map((measureLabel) => (
            <span
              key={measureLabel}
              data-trainability-label
              className={`block ${TRAINABILITY_LABEL_MEASURE_CLASS}`}
            >
              {measureLabel}
            </span>
          ))}
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${
                isDarkMode
                  ? "bg-slate-700/70 text-slate-100 ring-1 ring-white/10"
                  : "bg-white/80 text-slate-700 ring-1 ring-slate-300/80"
              }`}
            >
              <Brain className="h-6 w-6" />
            </span>

            <div>
              <h2 className="text-xl md:text-2xl font-semibold leading-tight">
                {t("metrics.trainability")}
              </h2>
              <p className="text-sm md:text-base opacity-75">
                {t("metrics.trainability_subtitle")}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className={`rounded-lg px-2 py-1.5 ${isDarkMode ? "bg-white/10" : "bg-white/75"}`}>
              <div className="opacity-70">{t("metric_card.players", { defaultValue: "Players" })}</div>
              <div className="text-sm font-semibold tabular-nums">{entries.length}</div>
            </div>
            <div className={`rounded-lg px-2 py-1.5 ${isDarkMode ? "bg-white/10" : "bg-white/75"}`}>
              <div className="opacity-70">{t("metrics.average", { defaultValue: "Average" })}</div>
              <div className="text-sm font-semibold tabular-nums">{avgScore10.toFixed(1)}/10</div>
            </div>
            <div className={`rounded-lg px-2 py-1.5 ${isDarkMode ? "bg-rose-500/15" : "bg-rose-100/80"}`}>
              <div className="opacity-80">{t("team_overview.risk_mode_at_risk", { defaultValue: "At-risk" })}</div>
              <div className={`text-sm font-semibold tabular-nums ${isDarkMode ? "text-rose-200" : "text-rose-700"}`}>
                {atRiskCount}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 2xl:grid-cols-2 gap-6">
          <div>
            <div className="mb-2 text-[11px] md:text-xs uppercase tracking-[0.18em] opacity-70">
              {t("metric_card.top_values")}
            </div>
            <div className="space-y-2.5">
              {topVisible.map((entry, index) => renderRow(entry, "top", index))}
            </div>
          </div>

          <div>
            <div className="mb-2 text-[11px] md:text-xs uppercase tracking-[0.18em] opacity-70">
              {t("metric_card.lowest_values")}
            </div>
            <div className="space-y-2.5">
              {lowestVisible.map((entry, index) => renderRow(entry, "lowest", index))}
            </div>
          </div>
        </div>

        {hasHiddenEntries && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                isDarkMode ? "text-slate-200 hover:text-white" : "text-slate-700 hover:text-slate-900"
              }`}
            >
              {expanded ? t("metric_card.collapse") : t("metric_card.expand")}
              {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
